/** The easy flow's server state: ONE react-query poll of the aggregate endpoint (A5) per
 *  tenant, shared by the step header, the screens and the tray. It polls every `pollMs`
 *  (3 s) and stops once nothing will move on its own: no lane running, the fallback admin not
 *  pending, no go live re-syncing, and the phase is `ready` — or `importing` with an `error` attention item and no
 *  import still bound to run (the import is stalled; `isStalled`). A user
 *  action (`resume`, from `useResumePolling`) polls again for `resumeMs`, since it may start
 *  work that the settled state does not show yet. A failed read stops the poll only when it is
 *  a 4xx (no permission, not an easy tenant, gone); a 5xx or a network error (a deploy, a blip)
 *  backs off — `backoffMs`, doubling per failed read up to `maxBackoffMs` — and polls on, and
 *  `reconnecting` says so. Tests pass a short `pollMs` / `backoffMs` instead of faking timers
 *  under MSW. */
import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as easyApi from '@/api/easyOnboarding';
import type { EasyState } from '@/api/easyOnboarding';

export const POLL_MS = 3000;
/** How long a user action polls again even over a settled state. */
export const RESUME_MS = 30_000;
/** The first wait after a failed read (5xx / network); it doubles per failure in a row. */
export const BACKOFF_MS = 2000;
export const MAX_BACKOFF_MS = 30_000;

/** A refusal that will not change on a retry (4xx): the poll stops. */
export function isClientError(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status >= 400 && e.status < 500;
}

/** The wait before the next read after `failures` failed reads in a row (1 → `base`). */
export function backoffDelay(failures: number, base = BACKOFF_MS, max = MAX_BACKOFF_MS): number {
  return Math.min(max, base * 2 ** Math.max(0, failures - 1));
}

const ACTIVE_TASK_STATES: ReadonlySet<string> = new Set(['pending', 'running', 'awaiting_approval']);

/** An import that cannot move on: still `importing`, with an item that failed, no lane running
 *  and no lane task pending, running or awaiting approval (the spec's definition) — a retry
 *  under way is not stalled. Nor is a selection whose import is still bound to run
 *  (`selection.locked`): in the hand-off between the catalogue import and the sample bookings
 *  no lane runs for a moment, and an unrelated failed item (messaging, say) must not stop the
 *  poll and offer a Finish the backend refuses (409 `import_running`). The backend uses the
 *  same rule. */
export function isStalled(s: EasyState | undefined): boolean {
  return (
    !!s &&
    s.phase === 'importing' &&
    !s.selection.locked &&
    s.attention.some((a) => a.kind === 'error') &&
    !s.lanes.some(
      (l) => l.state === 'running' || l.tasks.some((t) => ACTIVE_TASK_STATES.has(t.state)),
    )
  );
}

/** Nothing will change without a person: stop polling. `fallback_admin_pending`: Ready is
 *  committed before the fallback admin is written (or while its invite is sent), so a Ready read
 *  with `fallback_admin: null` is "not yet", not "none". A go live that is re-syncing
 *  (`go_live.status == running`) moves on its own too: the poll follows it to done or failed. */
export function isSettledState(s: EasyState | undefined): boolean {
  if (
    !s ||
    s.fallback_admin_pending ||
    s.go_live.status === 'running' ||
    s.lanes.some((l) => l.state === 'running')
  )
    return false;
  return s.phase === 'ready' || isStalled(s);
}

const ResumeContext = createContext<() => void>(() => {});
/** Wraps the screens so their actions can restart a stopped poll. */
export const ResumePollingProvider = ResumeContext.Provider;
/** Call after a user action that may start server work (a selection, a retry, finish). */
export function useResumePolling(): () => void {
  return useContext(ResumeContext);
}

function retryable(count: number, e: unknown): boolean {
  // A 4xx (no permission, not an easy tenant, not found) will not change on a retry.
  if (isClientError(e)) return false;
  return count < 2;
}

export function useEasyState(
  topId: number | null,
  {
    enabled = true,
    pollMs = POLL_MS,
    resumeMs = RESUME_MS,
    backoffMs = BACKOFF_MS,
    maxBackoffMs = MAX_BACKOFF_MS,
  } = {},
) {
  // A new value re-creates the interval function, which restarts a stopped poll.
  const [resumedAt, setResumedAt] = useState(0);
  // The query's error count at its last good read: the difference is the failures in a row.
  // Null until the first read (a remount may find older errors counted in the cache).
  const errorsAtLastRead = useRef<number | null>(null);
  const query = useQuery({
    queryKey: easyApi.easyStateKey(topId ?? 0),
    queryFn: ({ signal }) => easyApi.getState(topId as number, signal),
    enabled: enabled && topId != null,
    retry: retryable,
    refetchInterval: (q) => {
      const { error, errorUpdateCount } = q.state;
      if (error) {
        if (isClientError(error)) return false;
        errorsAtLastRead.current ??= errorUpdateCount - 1;
        return backoffDelay(errorUpdateCount - errorsAtLastRead.current, backoffMs, maxBackoffMs);
      }
      errorsAtLastRead.current = errorUpdateCount;
      const resumed = Date.now() - resumedAt < resumeMs;
      return isSettledState(q.state.data) && !resumed ? false : pollMs;
    },
  });
  const resume = useCallback(() => setResumedAt(Date.now()), []);
  // A transient failure the poll is riding out (it keeps the last good state on screen).
  const reconnecting = query.error != null && !isClientError(query.error);
  return { query, resume, reconnecting };
}

/** Can this environment run the flow? Read once per visit (cheap; never calls a vendor). */
export function useEasyStatus(enabled = true) {
  return useQuery({
    queryKey: easyApi.easyStatusKey,
    queryFn: ({ signal }) => easyApi.getStatus(signal),
    enabled,
    retry: retryable,
    staleTime: 30_000,
  });
}

/** The permissions every easy endpoint requires. */
export const EASY_PERMS = ['console_top_manage', 'console_preflight_manage'] as const;
